import { useEffect, useState } from "react";
import { Music2, Radio, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, StatCard, TH, TD, Badge } from "./AdminLayout";
import { Switch } from "../../components/ui/switch";

export default function MusicAdmin() {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/music").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return <p className="text-white/40 font-mono text-sm">Tuning in…</p>;

  const toggleSource = async (name, enabled) => {
    try {
      await api.patch("/admin/music/sources", { name, enabled });
      toast.success(`${name} ${enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-music">
      <PageTitle title="Music Management" sub="Live playback across the fleet (sample data)." />

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-lg border border-blurple/30 bg-blurple/[0.06] p-6 md:col-span-2" data-testid="music-now-playing">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-3 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Now playing
          </p>
          <p className="font-heading text-xl font-bold text-white">{data.now_playing.title} <span className="text-white/40 font-normal">— {data.now_playing.artist}</span></p>
          <p className="text-sm text-muted-foreground mt-2 font-mono">{data.now_playing.duration} · requested by {data.now_playing.requested_by} · {data.now_playing.guild}</p>
        </div>
        <StatCard label="Playback errors (24h)" value={data.playback_errors_24h} icon={AlertTriangle} tone="text-amber-400" testId="music-errors" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="rounded-lg border border-white/5 bg-panel overflow-hidden" data-testid="music-queues">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold px-5 py-4 flex items-center gap-2"><Radio size={14} className="text-volt" /> Active queues</p>
          <table className="w-full">
            <thead className="border-t border-white/5"><tr><TH>Server</TH><TH>Tracks</TH><TH>Current</TH></tr></thead>
            <tbody className="divide-y divide-white/5">
              {data.active_queues.map((q) => (
                <tr key={q.guild}>
                  <TD className="text-white font-semibold">{q.guild}</TD>
                  <TD className="font-mono">{q.tracks}</TD>
                  <TD className="text-white/60 text-xs">{q.current}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-lg border border-white/5 bg-panel p-5" data-testid="music-sources">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4 flex items-center gap-2"><Music2 size={14} className="text-blurple" /> Music providers</p>
          <div className="space-y-3">
            {data.sources.map((s) => (
              <div key={s.name} className="flex items-center justify-between rounded-md border border-white/5 bg-white/[0.02] px-4 py-3" data-testid={`source-${s.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="text-sm text-white flex items-center gap-3">{s.name} {s.enabled ? <Badge tone="green">on</Badge> : <Badge tone="grey">off</Badge>}</span>
                <Switch checked={s.enabled} onCheckedChange={(v) => toggleSource(s.name, v)} data-testid={`source-toggle-${s.name.toLowerCase().replace(/\s+/g, "-")}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-white/5 bg-panel overflow-hidden" data-testid="music-top-songs">
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold px-5 py-4">Most-played songs</p>
        <table className="w-full">
          <thead className="border-t border-white/5"><tr><TH>Track</TH><TH>Artist</TH><TH>Plays</TH></tr></thead>
          <tbody className="divide-y divide-white/5">
            {data.top_songs.map((s) => (
              <tr key={s.title}><TD className="text-white">{s.title}</TD><TD>{s.artist}</TD><TD className="font-mono">{s.plays.toLocaleString()}</TD></tr>
            ))}
          </tbody>
        </table>
        </div>

        <div className="rounded-lg border border-white/5 bg-panel overflow-hidden" data-testid="music-top-albums">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold px-5 py-4">Most-played albums</p>
          <table className="w-full">
            <thead className="border-t border-white/5"><tr><TH>Album</TH><TH>Artist</TH><TH>Plays</TH></tr></thead>
            <tbody className="divide-y divide-white/5">
              {(data.top_albums || []).map((a) => (
                <tr key={a.title}><TD className="text-white">{a.title}</TD><TD>{a.artist}</TD><TD className="font-mono">{a.plays.toLocaleString()}</TD></tr>
              ))}
              {!(data.top_albums || []).length && <tr><TD className="text-white/40" colSpan={3}>No album data yet.</TD></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
